/**
 * Predictive search dropdown — Shopify Predictive Search API.
 *
 * The results markup is rendered server-side by
 * sections/predictive-search.liquid, so search result cards reuse the exact
 * same snippets as the rest of the theme.
 */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.reset = this.querySelector('[data-search-reset]');
    if (!this.input || !this.results) return;

    this.abortController = null;
    this.cache = new Map();
    this.minLength = parseInt(this.dataset.minLength || '2', 10);

    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('aria-expanded', 'false');

    this.input.addEventListener('input', Theme.debounce(() => this.onInput(), 250));
    this.input.addEventListener('focus', () => {
      if (this.input.value.length >= this.minLength) this.onInput();
    });
    this.addEventListener('keydown', (event) => this.onKeydown(event));

    if (this.reset) {
      this.reset.addEventListener('click', (event) => {
        event.preventDefault();
        this.input.value = '';
        this.close();
        this.input.focus();
      });
    }

    // Close when focus or a click leaves the component.
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });
  }

  onInput() {
    const query = this.input.value.trim();
    if (this.reset) this.reset.hidden = query.length === 0;

    if (query.length < this.minLength) {
      this.close();
      return;
    }
    this.search(query);
  }

  async search(query) {
    if (this.cache.has(query)) {
      this.render(this.cache.get(query));
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.setLoading(true);

    const params = new URLSearchParams({
      q: query,
      'resources[type]': this.dataset.resources || 'product,collection,article,page',
      'resources[limit]': this.dataset.limit || '6',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
      section_id: 'predictive-search',
    });

    try {
      const response = await fetch(
        Theme.routes.predictive_search_url + '?' + params.toString(),
        { signal: this.abortController.signal }
      );
      if (!response.ok) throw new Error('Search failed');
      const text = await response.text();
      // Section rendering wraps output in the section div; take its contents.
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const inner = parsed.querySelector('[data-predictive-search-results]');
      const html = inner ? inner.innerHTML : text;

      this.cache.set(query, html);
      this.render(html);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // A failed prediction should never block the real search — let the form submit.
      this.close();
    } finally {
      this.setLoading(false);
    }
  }

  render(html) {
    this.results.innerHTML = html;
    this.open();
    this.announce();
  }

  announce() {
    if (!this.status) return;
    const count = this.results.querySelectorAll('[data-search-result]').length;
    const template = count === 1 ? Theme.strings.searchResult : Theme.strings.searchResults;
    this.status.textContent = (template || '{{ count }} results').replace('{{ count }}', count);
  }

  setLoading(state) {
    this.toggleAttribute('data-loading', state);
    this.input.setAttribute('aria-busy', String(state));
  }

  open() {
    this.setAttribute('data-open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.results.hidden = false;
  }

  close() {
    this.removeAttribute('data-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.results.hidden = true;
    this.activeIndex = -1;
  }

  /** Arrow-key navigation across rendered results. */
  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      this.input.focus();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const items = Array.from(this.results.querySelectorAll('[data-search-result] a, a[data-search-result]'));
    if (!items.length) return;
    event.preventDefault();

    const current = items.indexOf(document.activeElement);
    let next;
    if (event.key === 'ArrowDown') {
      next = current < 0 ? 0 : Math.min(current + 1, items.length - 1);
    } else {
      next = current <= 0 ? -1 : current - 1;
    }

    if (next < 0) {
      this.input.focus();
    } else {
      items[next].focus();
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
